import to from 'await-to-js';
import {
  BodyParam,
  AddCartStatusInfo,
  OfficialDocInfo,
  CartData,
} from '@/apis/cart/cart.interface';
import { Response } from '@/apis/common.types';
import { addCart } from '@/apis/cart/cart.api';
import { getUserUUIDFromCookie } from '@/utils/userUtils';
import i18next from 'i18next';
import UKToast from '@/components/UKToast/index';
import { gtagAddCartLog } from '@/intl-jdi-common-ssr/utils/thirdParty/gtag';

interface CartProps {
  result?: Response<CartData>;
  isDefinedToast?: boolean;
  err?: Error | null;
}

export type CartResProps = CartProps | undefined;

/**
 * 加入购物车
 * @param productList - [{ skuId: 88, num: "1" }] 加入购物车所需的参数
 * @param isMultiProduct -  是否是整单加车 目前订单再次购买为整单加车
 * @param shipmentType - 配送方式
 * @param isNeedCustomToast - 是否需要自定义toast
 */
export const addCartUtil = async (
  productList: any[],
  isMultiProduct = false,
  shipmentType?: string,
  isNeedCustomToast = false,
): Promise<CartResProps> => {
  const formatProductList = productList.map((item) => {
    item.itemType = 1;
    return item;
  });
  const bodyParam: BodyParam = {
    verticalTag: 'cn_ybxt_b2c',
    operations: { products: formatProductList },
    cartuuid: getUserUUIDFromCookie(),
    userActionId: 'addCart',
    extendParamMap: {
      stock: true,
    },
  };
  if (shipmentType) {
    bodyParam['shipmentType'] = shipmentType;
  }
  const [err, result] = await to(addCart(bodyParam));
  //"Failed to add to cart. The product is no longer available"
  if (err) {
    UKToast.show({
      content: i18next.t('cart.addCartFailed'), // 弹窗内容
      icon: 'error', // 可选：success, fail, loading, warn, info，影响图标
    });
    return Promise.reject(err);
  } else {
    const isDefinedToast = isAddCartFailed(result); // 订单再次加车商品失败需要主动触发业务弹窗
    if (isDefinedToast && isMultiProduct) {
      return { result, isDefinedToast, err };
    }
    if (result?.code === '200') {
      gtagAddCartLog(productList?.map((item) => item.skuId));
      const isSaleLimitText = isSaleLimit(result);
      if (isSaleLimitText) {
        UKToast.show({
          content: isSaleLimitText,
          icon: 'warning',
        });
      } else if (!isNeedCustomToast) {
        // 整单加车成功不提示，直接跳转购物车
        UKToast.show({
          content: i18next.t('cart.addCartSuccess'), // 弹窗内容
          icon: 'success', // 可选：success, fail, loading, warn, info，影响图标
        });
      }
      return { result };
    }
    if (isNeedCustomToast) {
      return { result };
    }
    UKToast.show({
      content: result?.msg || i18next.t('cart.addCartWarning'), // 弹窗内容
      icon: 'warning', // 可选：success, fail, loading, warn, info，影响图标
    });
  }
};

const isAddCartFailed = (result: any) => {
  const cartData = result?.data;
  if (cartData) {
    // 商品加车失败楼层
    const addCartFailed = Object.values(cartData?.data).filter((item: any) => {
      if (
        'type' in item &&
        item.type &&
        item.type.namespace === 'jd_main-DM#cartView.addCartCheckResult-M#basic'
      ) {
        return true;
      }
      return false;
    }) as Array<{ info: AddCartStatusInfo }>;
    if (addCartFailed && addCartFailed.length > 0) {
      const failedItems = addCartFailed[0].info?.['HJM-M#basic&basic']?.checkNoStockSkuList;
      return failedItems && failedItems.length > 0;
    }
  }
  return false;
};

// 是否提示限购文案
const isSaleLimit = (result: any) => {
  const cartData = result?.data;
  if (cartData) {
    const saleLimit = Object.values(cartData?.data).filter((item: any) => {
      if (item?.type?.namespace === 'core.trade-DM#cartView.officialDoc-M#officialDoc') {
        return true;
      }
      return false;
    }) as Array<{ info: OfficialDocInfo }>;
    if (saleLimit && saleLimit.length > 0) {
      const windowText = saleLimit[0].info?.['C-M#officialDoc&popUpWindow']?.windowText;
      return windowText;
    }
  }
  return '';
};
